// src/lib/visitApi.ts
// Helper for Visit Bergamo NextAPI calls
export type VisitSyncSuccess = {
  ok: true;
  provisioned: boolean;
  needsTokenRefresh: boolean;
  auditId?: string;
  status: number;
  raw?: unknown;
};

export type VisitSyncFailure = {
  ok: false;
  status?: number;
  errorText: string;
};

export type VisitSyncResponse = VisitSyncSuccess | VisitSyncFailure;

/**
 * Call POST /nextapi/visit/users/sync with a Firebase ID token.
 * The endpoint is expected to provision the user and optionally request a token refresh.
 */
export async function syncVisitUser(
  firebaseIdToken: string,
  fetchImpl: typeof fetch = fetch
): Promise<VisitSyncResponse> {
  try {
    const res = await fetchImpl('/nextapi/visit/users/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${firebaseIdToken}`,
      },
      body: JSON.stringify({}),
    });

    if (!res.ok) {
      const errorText = await safeReadText(res);
      return { ok: false, status: res.status, errorText: errorText || 'Request failed' };
    }

    let data: any = null;
    try {
      data = await res.json();
    } catch (e) {
      return { ok: false, status: res.status, errorText: 'Invalid JSON response' };
    }

    const provisioned = Boolean(data?.provisioned);
    const needsTokenRefresh = Boolean(data?.needsTokenRefresh);
    const auditId = typeof data?.auditId === 'string' ? data.auditId : undefined;

    return {
      ok: true,
      provisioned,
      needsTokenRefresh,
      auditId,
      status: res.status,
      raw: data,
    };
  } catch (error: any) {
    return { ok: false, errorText: error?.message || 'Network error' };
  }
}

async function safeReadText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}
